import React, { useState, useEffect } from 'react';
import { BRAND } from '../config/branding';
import axiosClient from '../api/axiosClient';

function CertificateLifecycleDashboard() {
  const [counts, setCounts] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchCounts();
    fetchCertificates();
  }, []);

  useEffect(() => {
    fetchCertificates();
  }, [selectedStatus, search]);

  const fetchCounts = async () => {
    try {
      const response = await axiosClient.get('/certifications/certifications/stats/');
      setCounts(response.data);
    } catch (err) {
      console.error('Error fetching certificate counts:', err);
    }
  };

  const fetchCertificates = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedStatus !== 'all') params.status = selectedStatus;
      const response = await axiosClient.get('/certifications/certifications/', { params });
      const all = Array.isArray(response.data) ? response.data : [];
      const filtered = search
        ? all.filter(c =>
            (c.company_name || '').toLowerCase().includes(search.toLowerCase()) ||
            (c.iso_standard || '').toLowerCase().includes(search.toLowerCase()) ||
            (c.certificate_number || '').toLowerCase().includes(search.toLowerCase()))
        : all;
      setCertificates(filtered);
    } catch (err) {
      console.error('Error fetching certificates:', err);
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' };
      case 'issued':
        return { bg: '#d4edda', text: '#155724', border: '#c3e6cb' };
      case 'expiring_soon':
        return { bg: '#fff3cd', text: '#856404', border: '#ffeaa7' };
      case 'expired':
        return { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' };
      case 'suspended':
        return { bg: '#d6d8db', text: '#383d41', border: '#c6c8ca' };
      case 'withdrawn':
        return { bg: '#f8d7da', text: '#721c24', border: '#f5c6cb' };
      default:
        return { bg: '#e2e3e5', text: '#383d41', border: '#d6d8db' };
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'issued':
        return 'Active';
      case 'expiring_soon':
        return 'Expiring Soon';
      case 'expired':
        return 'Expired';
      case 'suspended':
        return 'Suspended';
      case 'withdrawn':
        return 'Withdrawn';
      default:
        return status;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px' }}>
        <h1 style={{ margin: 0, fontSize: '28px', color: '#333' }}>Certificate Lifecycle Dashboard</h1>
        <p style={{ color: '#666', marginTop: '8px' }}>
          Monitor certificate statuses and lifecycle across all certification bodies
        </p>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #f5c6cb'
        }}>
          {error}
        </div>
      )}

      {/* Status Cards */}
      {counts && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '30px'
        }}>
          <StatusCard
            title="Pending"
            count={counts.pending}
            color="#ffc107"
            icon="⏳"
            onClick={() => setSelectedStatus('pending')}
            active={selectedStatus === 'pending'}
          />
          <StatusCard
            title="Active"
            count={counts.issued}
            color="#28a745"
            icon="✓"
            onClick={() => setSelectedStatus('issued')}
            active={selectedStatus === 'issued'}
          />
          <StatusCard
            title="Expiring Soon"
            count={counts.expiring_soon}
            color="#ff9800"
            icon="⚠"
            onClick={() => setSelectedStatus('expiring_soon')}
            active={selectedStatus === 'expiring_soon'}
          />
          <StatusCard
            title="Expired"
            count={counts.expired}
            color="#dc3545"
            icon="✗"
            onClick={() => setSelectedStatus('expired')}
            active={selectedStatus === 'expired'}
          />
          <StatusCard
            title="Suspended"
            count={counts.suspended}
            color="#6c757d"
            icon="⊘"
            onClick={() => setSelectedStatus('suspended')}
            active={selectedStatus === 'suspended'}
          />
          <StatusCard
            title="Withdrawn"
            count={counts.withdrawn}
            color="#dc3545"
            icon="⊗"
            onClick={() => setSelectedStatus('withdrawn')}
            active={selectedStatus === 'withdrawn'}
          />
        </div>
      )}

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #dee2e6',
        display: 'flex',
        gap: '15px',
        alignItems: 'center',
        flexWrap: 'wrap'
      }}>
        <div style={{ flex: '1', minWidth: '250px' }}>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#333' }}>
            Search
          </label>
          <input
            type="text"
            placeholder="Search by certificate number or company..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%',
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px'
            }}
          />
        </div>

        <div>
          <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '600', color: '#333' }}>
            Filter by Status
          </label>
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            style={{
              padding: '10px',
              border: '1px solid #ced4da',
              borderRadius: '4px',
              fontSize: '14px',
              minWidth: '180px'
            }}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="issued">Active</option>
            <option value="expiring_soon">Expiring Soon</option>
            <option value="expired">Expired</option>
            <option value="suspended">Suspended</option>
            <option value="withdrawn">Withdrawn</option>
          </select>
        </div>

        {(selectedStatus !== 'all' || search) && (
          <button
            onClick={() => {
              setSelectedStatus('all');
              setSearch('');
            }}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              alignSelf: 'flex-end'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Certificates Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '8px',
        border: '1px solid #dee2e6',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #dee2e6',
          backgroundColor: '#f8f9fa'
        }}>
          <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
            Certificates ({certificates.length})
          </h2>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading certificates...
          </div>
        ) : certificates.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            No certificates found matching your filters.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Certificate Number
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Company
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    ISO Standard
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Issue Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Expiry Date
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Days Until Expiry
                  </th>
                  <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600' }}>
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {certificates.map((cert) => {
                  const statusColors = getStatusColor(cert.lifecycle_status);
                  return (
                    <tr key={cert.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                      <td style={{ padding: '12px', fontSize: '14px', fontFamily: 'monospace', fontWeight: '500' }}>
                        {cert.certificate_number}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {cert.company_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {cert.iso_standard_name || 'N/A'}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {formatDate(cert.issue_date)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {formatDate(cert.expiry_date)}
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px' }}>
                        {cert.days_until_expiry !== null ? (
                          <span style={{
                            color: cert.days_until_expiry < 0 ? '#dc3545' :
                                   cert.days_until_expiry < 60 ? '#ff9800' : '#28a745'
                          }}>
                            {cert.days_until_expiry < 0 ? 'Expired' : `${cert.days_until_expiry} days`}
                          </span>
                        ) : (
                          'N/A'
                        )}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          backgroundColor: statusColors.bg,
                          color: statusColors.text,
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: `1px solid ${statusColors.border}`
                        }}>
                          {getStatusLabel(cert.lifecycle_status)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusCard({ title, count, color, icon, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        border: active ? `2px solid ${color}` : '1px solid #dee2e6',
        cursor: 'pointer',
        transition: 'all 0.2s',
        boxShadow: active ? `0 4px 12px rgba(0,0,0,0.1)` : 'none'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.boxShadow = 'none';
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
        <div style={{ fontSize: '14px', color: '#666', fontWeight: '600' }}>
          {title}
        </div>
        <div style={{ fontSize: '24px' }}>
          {icon}
        </div>
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: color }}>
        {count}
      </div>
    </div>
  );
}

export default CertificateLifecycleDashboard;
